import { ITooltipParams } from "ag-grid-community";
import { logger } from "@platform/browser-logger";

import { MAX_DOLLAR_AMOUNT } from "./constants";
import {
  formatBenefitDescriptionOption,
  formatPopoverListEntry,
} from "./cellSpecUtils";

import type { CellSpec } from "./types";

const TOOLTIP_CLICK_TO_CONFIGURE = "Click to configure";

export const preExistingConditionMonthsDetail: CellSpec = {
  cellType: "custom",
  dataType: "string",
  cellRenderer: "PreExistingConditionMonthsRenderer",
  cellEditor: "PreExistingConditionMonthsEditor",
  singleClickEdit: true,
  description:
    "Pre-existing condition months detail - format XX/XX or XX/XX/XX",
  validation: {
    required: false,
  },
};

export const ageReductionScheduleDetails: CellSpec = {
  cellType: "popoverList",
  dataType: "array",
  cellRenderer: "PopoverListRenderer",
  cellEditor: "AgeReductionScheduleEditor",
  singleClickEdit: true,
  editable: true,
  valueGetter: (params) => {
    const value = params.data?.[params.colDef.field || ""] as string;
    if (typeof value === "string" && value.trim().startsWith("[")) {
      try {
        return JSON.parse(value) as [];
      } catch {
        return [];
      }
    }
    return Array.isArray(value) ? value : [];
  },
  valueSetter: (params) => {
    const newValue = Array.isArray(params.newValue) ? params.newValue : [];
    params.data[params.colDef.field || ""] = newValue;
    return true;
  },
  tooltipValueGetter: (params: ITooltipParams) => {
    const value: unknown = params.value;
    if (!Array.isArray(value) || value.length === 0) {
      return TOOLTIP_CLICK_TO_CONFIGURE;
    }
    const fields = [{ key: "reductionPercent" }, { key: "age" }];
    const formatted = value
      .map((entry: unknown) =>
        formatPopoverListEntry(
          entry as Record<string, unknown>,
          fields,
          "XX% at XX",
        ),
      )
      .join(", ");
    return formatted || TOOLTIP_CLICK_TO_CONFIGURE;
  },
  cellEditorParams: {
    title: "Age Reduction Schedule",
    ageReductionMode: true,
    renderFormat: "XX% at XX",
    fields: [
      {
        key: "reductionPercent",
        label: "Reduction Percentage",
        type: "percentage" as const,
        min: 1,
        max: 100,
        placeholder: "e.g., 50",
      },
      {
        key: "age",
        label: "Age",
        type: "number" as const,
        min: 1,
        max: 120,
        placeholder: "e.g., 65",
      },
    ],
  },
  description: "Age reduction schedule configuration",
  defaultValue: [{ reductionPercent: 50, age: 65 }],
  validation: {
    required: false,
  },
};

export const benefitDescriptionMultiOptionsPopover: CellSpec = {
  cellType: "custom",
  dataType: "array",
  cellRenderer: "BenefitDescriptionMultiOptionsRenderer",
  cellEditor: "BenefitDescriptionMultiOptionsEditor",
  singleClickEdit: true,
  editable: true,
  valueGetter: (params) => {
    const value = params.data?.[params.colDef.field || ""] as string;

    if (typeof value === "string" && value.trim().startsWith("[")) {
      try {
        return JSON.parse(value) as [];
      } catch {
        return [];
      }
    }

    if (Array.isArray(value)) {
      return value;
    }

    return [];
  },
  valueSetter: (params) => {
    const newValue = Array.isArray(params.newValue) ? params.newValue : [];
    params.data[params.colDef.field || ""] = newValue;
    return true;
  },
  tooltipValueGetter: (params: ITooltipParams) => {
    const value: unknown = params.value;
    if (!Array.isArray(value) || value.length === 0) {
      return TOOLTIP_CLICK_TO_CONFIGURE;
    }
    const formatted = value
      .map((option) =>
        formatBenefitDescriptionOption(
          option as { type: string; value: number },
        ),
      )
      .join(", ");
    return formatted || TOOLTIP_CLICK_TO_CONFIGURE;
  },
  cellEditorParams: {
    maxEntries: 10,
    title: "Benefit Description Options",
  },
  description:
    "Benefit description with multiple dollar amounts and/or salary multipliers",
  validation: {
    required: false,
  },
};

export const multipleAmounts: CellSpec = {
  cellType: "popoverList",
  dataType: "array",
  cellRenderer: "PopoverListRenderer",
  cellEditor: "PopoverListEditor",
  singleClickEdit: true,
  editable: true,
  valueGetter: (params) => {
    const value = params.data?.[params.colDef.field || ""] as string;
    if (typeof value === "string" && value.trim().startsWith("[")) {
      try {
        return JSON.parse(value) as [];
      } catch {
        return [];
      }
    }
    return Array.isArray(value) ? value : [];
  },
  valueSetter: (params) => {
    const newValue = Array.isArray(params.newValue) ? params.newValue : [];
    params.data[params.colDef.field || ""] = newValue;
    return true;
  },
  tooltipValueGetter: (params: ITooltipParams) => {
    const value: unknown = params.value;
    if (!Array.isArray(value) || value.length === 0) {
      return TOOLTIP_CLICK_TO_CONFIGURE;
    }
    const fields = [{ key: "amount" }];
    const formatted = value
      .map((entry: unknown) =>
        formatPopoverListEntry(entry as Record<string, unknown>, fields, "$XX"),
      )
      .join(", ");
    return formatted || TOOLTIP_CLICK_TO_CONFIGURE;
  },
  cellEditorParams: {
    title: "Benefit Amounts",
    renderFormat: "$XX",
    inputFormat: "$XX",
    fields: [
      {
        key: "amount",
        label: "Amount",
        type: "currency" as const,
        min: 0,
        max: MAX_DOLLAR_AMOUNT,
        placeholder: "e.g., 10000",
      },
    ],
  },
  description: "Configure multiple dollar amount options",
  validation: {
    required: false,
    maximum: MAX_DOLLAR_AMOUNT,
  },
};

export const multipleDollarAmountsPopover: CellSpec = {
  cellType: "popoverList",
  dataType: "array",
  cellRenderer: "PopoverListRenderer",
  cellEditor: "PopoverListEditor",
  singleClickEdit: true,
  editable: true,
  valueGetter: (params) => {
    const value = params.data?.[params.colDef.field || ""] as string;
    if (typeof value === "string" && value.trim().startsWith("[")) {
      try {
        return JSON.parse(value) as [];
      } catch {
        return [];
      }
    }
    return Array.isArray(value) ? value : [];
  },
  valueSetter: (params) => {
    const newValue = Array.isArray(params.newValue) ? params.newValue : [];
    params.data[params.colDef.field || ""] = newValue;
    return true;
  },
  tooltipValueGetter: (params: ITooltipParams) => {
    const value: unknown = params.value;
    if (!Array.isArray(value) || value.length === 0) {
      return TOOLTIP_CLICK_TO_CONFIGURE;
    }
    const fields = [{ key: "amount" }];
    const formatted = value
      .map((entry: unknown) =>
        formatPopoverListEntry(entry as Record<string, unknown>, fields, "$XX"),
      )
      .join(", ");
    return formatted || TOOLTIP_CLICK_TO_CONFIGURE;
  },
  cellEditorParams: {
    title: "Dollar Amounts",
    renderFormat: "$XX",
    inputFormat: "$XX",
    minEntries: 2,
    maxEntries: 10,
    fields: [
      {
        key: "amount",
        label: "Amount",
        type: "currency" as const,
        min: 0,
        max: MAX_DOLLAR_AMOUNT,
        placeholder: "e.g., 10000",
      },
    ],
  },
  description: "Configure multiple dollar amount options (2-10 entries)",
  validation: {
    required: false,
    maximum: MAX_DOLLAR_AMOUNT,
  },
};

export const multipleSalaryMultipliersPopover: CellSpec = {
  cellType: "popoverList",
  dataType: "array",
  cellRenderer: "PopoverListRenderer",
  cellEditor: "PopoverListEditor",
  singleClickEdit: true,
  editable: true,
  valueGetter: (params) => {
    const value = params.data?.[params.colDef.field || ""] as string;
    if (typeof value === "string" && value.trim().startsWith("[")) {
      try {
        return JSON.parse(value) as [];
      } catch {
        return [];
      }
    }
    return Array.isArray(value) ? value : [];
  },
  valueSetter: (params) => {
    const newValue = Array.isArray(params.newValue) ? params.newValue : [];
    params.data[params.colDef.field || ""] = newValue;
    return true;
  },
  tooltipValueGetter: (params: ITooltipParams) => {
    const value: unknown = params.value;
    if (!Array.isArray(value) || value.length === 0) {
      return TOOLTIP_CLICK_TO_CONFIGURE;
    }
    const fields = [{ key: "amount" }];
    const formatted = value
      .map((entry: unknown) =>
        formatPopoverListEntry(
          entry as Record<string, unknown>,
          fields,
          "XXx salary",
        ),
      )
      .join(", ");
    return formatted || TOOLTIP_CLICK_TO_CONFIGURE;
  },
  cellEditorParams: {
    title: "Salary Multipliers",
    renderFormat: "XXx salary",
    inputFormat: "XX",
    minEntries: 2,
    maxEntries: 10,
    fields: [
      {
        key: "amount",
        label: "Multiplier",
        type: "number" as const,
        min: 0,
        max: 10,
        step: 0.5,
        placeholder: "e.g., 2",
      },
    ],
  },
  description: "Configure multiple salary multiplier options (2-10 entries)",
  validation: {
    required: false,
    maximum: 10,
  },
};

export const eligibilityClasses: CellSpec = {
  cellType: "custom",
  dataType: "array",
  cellRenderer: "EligibilityClassesRenderer",
  cellEditor: "EligibilityClassesEditor",
  singleClickEdit: true,
  editable: true,
  description: "Benefit classes assigned to this benefit plan group",
  valueGetter: (params) => {
    const planIdentifier = (params.colDef.field || "").split(".")[0];
    const value = params.data?.[planIdentifier] as
      | { name: string }[]
      | undefined;
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  },
  valueSetter: (params) => {
    const newValue: unknown = params.newValue;
    const field = params.colDef.field;

    if (!field) {
      return false;
    }

    const planIdentifier = field.split(".")[0];

    if (typeof newValue === "string") {
      logger.warn(
        "[EC_DEBUG] eligibilityClasses valueSetter - rejecting string value:",
        {
          rejectedValue: newValue,
          field,
        },
      );
      return false;
    }

    if (
      Array.isArray(newValue) ||
      newValue === undefined ||
      newValue === null
    ) {
      params.data[planIdentifier] = newValue;
      return true;
    }

    logger.warn(
      "[EC_DEBUG] eligibilityClasses valueSetter - rejecting invalid value:",
      {
        rejectedValue: newValue,
        rejectedType: typeof newValue,
        field,
      },
    );
    return false;
  },
  valueFormatter: (params) => {
    const value = params.value as { name: string }[] | string;
    if (!Array.isArray(value) || value.length === 0) {
      return "";
    }
    return value
      .map((cls) => cls.name)
      .filter(Boolean)
      .join(", ");
  },
  validation: {
    required: false,
  },
};

export const serviceWaitingPeriods: CellSpec = {
  cellType: "custom",
  dataType: "array",
  cellRenderer: "ServiceWaitingPeriodsRenderer",
  cellEditor: "ServiceWaitingPeriodsEditor",
  singleClickEdit: true,
  editable: true,
  description: "Configure service waiting periods",
  validation: {
    required: false,
  },
};
